import React, { useEffect, useMemo, useState } from "react";
import {
  AcademicCapIcon,
  CurrencyDollarIcon,
  PencilAltIcon,
  UserIcon,
} from "@heroicons/react/solid";
import UsersList from "../components/UsersList";
import ModernDatePicker from "../components/ModernDatePicker";
import { FiSave, FiSend } from "react-icons/fi";
import { apiFetch } from "../utils/apiClient";
import {
  findFormationMileageRate,
  calculateFormationTransport,
} from "../utils/formationMileage";

const inputClassName =
  "mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3.5 py-3 text-[15px] text-slate-800 shadow-sm outline-none transition-all duration-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100";

const labelClassName = "block text-[15px] font-medium text-slate-700";

const sectionCardClassName =
  "mt-5 w-full rounded-xl border border-gray-200 bg-white shadow-sm lg:w-4/5 xl:w-3/4";

const generateNumeroFormulaire = () => {
  const date = new Date();
  const yy = date.getFullYear().toString().slice(-2);
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  const randomNumber = Math.floor(1000 + Math.random() * 9000);

  return `CSV-${yy}${mm}${dd}${randomNumber}-PF`;
};

function SectionHeader({ icon: Icon, iconColor = "text-blue-400", children }) {
  return (
    <div className="mt-8 w-full rounded-t-xl bg-gradient-to-r from-[#182a43] to-[#40516a] py-3 shadow-sm">
      <p className="ml-4 flex items-center text-base font-semibold tracking-wide text-white">
        <Icon className={`h-4 w-4 ${iconColor} mr-2`} aria-hidden="true" />
        {children}
      </p>
    </div>
  );
}

function Field({
  label,
  name,
  value,
  onChange,
  type = "text",
  min,
  step,
  required = false,
  disabled = false,
  readOnly = false,
}) {
  return (
    <div className="w-full min-w-0">
      <label htmlFor={name} className={labelClassName}>
        {label}
        {required && <span className="text-xl text-red-900"> *</span>} :
      </label>
      <input
        id={name}
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        min={min}
        step={step}
        required={required}
        disabled={disabled}
        readOnly={readOnly}
        className={`${inputClassName} ${
          disabled || readOnly ? "bg-slate-50 text-slate-700" : "bg-white"
        }`}
      />
    </div>
  );
}

function ReadOnlyInfo({ label, value }) {
  return (
    <div className="min-w-0 py-2">
      <p className="text-[13px] font-semibold uppercase tracking-wide text-slate-600">
        {label}
      </p>
      <p className="mt-1 break-words text-[15px] font-medium text-slate-900" title={value}>
        {value || "—"}
      </p>
    </div>
  );
}

function YesNoField({ label, name, value, onChange, required = false }) {
  return (
    <div className="w-full min-w-0">
      <div className="grid grid-row-2 items-center gap-4">
        <label className={labelClassName}>
          {label}
          {required && <span className="text-xl text-red-900"> *</span>} :
        </label>
        <div className="flex flex-wrap items-center gap-3">
          {["oui", "non"].map((option) => {
            const isSelected = value === option;

            return (
              <label
                key={option}
                className={`flex min-w-24 items-center justify-center rounded-lg border px-6 py-2.5 text-sm font-medium cursor-pointer transition-all duration-200 focus-within:ring-2 focus-within:ring-blue-300 focus-within:ring-offset-1 ${
                  isSelected
                    ? "bg-blue-100 text-blue-600 border-blue-300"
                    : "bg-white text-gray-500 border-gray-300 hover:border-gray-400 hover:text-gray-600"
                }`}
              >
                <input
                  type="radio"
                  name={name}
                  value={option}
                  checked={isSelected}
                  onChange={onChange}
                  className="sr-only"
                  required={required}
                />
                {option.charAt(0).toUpperCase() + option.slice(1)}
              </label>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function MoneyField({ label, name, value, onChange }) {
  const handleAmountChange = (event) => {
    const normalizedValue = event.target.value
      .replace(",", ".")
      .replace(/[^0-9.]/g, "");
    const [integerPart, ...decimalParts] = normalizedValue.split(".");
    const amount =
      decimalParts.length > 0
        ? `${integerPart}.${decimalParts.join("")}`
        : integerPart;

    onChange({ target: { name, value: amount } });
  };

  return (
    <div className="flex w-full min-w-0 flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
      <label
        htmlFor={name}
        className="text-[15px] font-medium leading-5 text-slate-700"
      >
        {label} :
      </label>
      <div className="relative w-full flex-none sm:w-36 lg:w-40">
        <input
          id={name}
          type="text"
          name={name}
          value={value}
          onChange={handleAmountChange}
          inputMode="decimal"
          className="h-10 w-full rounded-lg border border-slate-300 bg-white py-2 pl-3 pr-9 text-right text-[15px] text-slate-800 shadow-sm outline-none transition-all duration-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
        />
        <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-slate-500">
          $
        </span>
      </div>
    </div>
  );
}

function ParticipationFormation({ user }) {
  const [formData, setFormData] = useState({
    numeroFormulaire: generateNumeroFormulaire(),
    dateSoumission: new Date().toISOString().split("T")[0],
    nom: "",
    courriel: "",
    lieuTravail: "",
    poste: "",
    managerInfo: {
      nom: "",
      email: "",
      userId: "",
      matricule: "",
    },
    directionSecteurSurintendance: {
      nom: "",
      email: "",
      userId: "",
      matricule: "",
    },
    descriptionObjectifs: "",
    ville: "",
    provincePays: "",
    dateDebut: "",
    nombreJours: "",
    voyageExterieurOntario: "",
    suppleanceRequise: "",
    nombreJoursSuppleance: "",
    suppleanceFondsCentraux: "",
    justificationFondsCentraux: "",
    coutInscription: "",
    kilometrageTransport: "",
    coutHebergement: "",
    coutRepas: "",
    coutSuppleance: "",
    autreDescription: "",
    coutAutre: "",
    signatureElectronique: "non",
  });

  const [mileageRates, setMileageRates] = useState([]);
  const [mileageStatus, setMileageStatus] = useState("loading");

  useEffect(() => {
    const controller = new AbortController();
    const fetchMileageRates = async () => {
      try {
        const response = await apiFetch(
          `${process.env.REACT_APP_API_BASE_URL}/TauxKilometrage`,
          { signal: controller.signal },
        );
        if (!response.ok) throw new Error("Mileage rates unavailable");
        const rates = await response.json();
        if (!Array.isArray(rates)) throw new Error("Invalid mileage rates");
        if (!controller.signal.aborted) {
          setMileageRates(rates);
          setMileageStatus("ready");
        }
      } catch (error) {
        if (!controller.signal.aborted) setMileageStatus("error");
      }
    };
    fetchMileageRates();
    return () => controller.abort();
  }, []);

  const mileageRate = findFormationMileageRate(mileageRates, formData.dateDebut);
  const coutTransport = calculateFormationTransport(
    formData.kilometrageTransport,
    mileageRate,
  );
  const transportPending =
    formData.kilometrageTransport !== "" && coutTransport === "";
  const mileageMessage = !formData.dateDebut
    ? "Sélectionnez la date de début pour connaître le taux applicable."
    : mileageStatus === "loading"
      ? "Chargement du taux kilométrique…"
      : mileageStatus === "error"
        ? "Impossible de charger les taux. Rechargez la page pour réessayer."
        : mileageRate === null
          ? "Aucun taux kilométrique n’est défini pour cette date de début."
          : `Taux applicable : ${new Intl.NumberFormat("fr-CA", {
              maximumFractionDigits: 6,
            }).format(mileageRate)} $/km — selon la date de début.`;

  useEffect(() => {
    const displayName = user?.displayName || "";

    setFormData((current) => ({
      ...current,
      nom: current.nom || displayName,
      courriel: current.courriel || user?.mail || user?.userPrincipalName || "",
      lieuTravail: current.lieuTravail || user?.officeLocation || "",
      poste: current.poste || user?.jobTitle || "",
    }));
  }, [user]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((current) => {
      const updated = { ...current, [name]: value };

      if (name === "suppleanceRequise" && value === "non") {
        updated.nombreJoursSuppleance = "";
        updated.suppleanceFondsCentraux = "non";
        updated.justificationFondsCentraux = "";
        updated.coutSuppleance = "";
      }

      if (name === "suppleanceFondsCentraux" && value === "non") {
        updated.justificationFondsCentraux = "";
      }

      return updated;
    });
  };

  const handleCertificationChange = (event) => {
    setFormData((current) => ({
      ...current,
      signatureElectronique: event.target.checked ? "oui" : "non",
    }));
  };

  const handleUserSelect = (selectedUser, fieldName) => {
    setFormData((current) => ({
      ...current,
      [fieldName]: {
        nom: selectedUser.displayName,
        email: selectedUser.mail,
        userId: selectedUser.id,
        matricule: selectedUser.employeeId,
      },
    }));
  };

  const total = useMemo(() => {
    return [
      "coutInscription",
      "coutHebergement",
      "coutRepas",
      "coutSuppleance",
      "coutAutre",
    ].reduce((sum, field) => {
      const amount = Number.parseFloat(formData[field]);
      return sum + (Number.isFinite(amount) ? amount : 0);
    }, Number(coutTransport || 0));
  }, [formData, coutTransport]);

  const formattedTotal = new Intl.NumberFormat("fr-CA", {
    style: "currency",
    currency: "CAD",
  }).format(total);

  return (
    <div className="relative min-h-screen bg-[#f7f7f8] px-4 py-6 sm:px-6 lg:px-8">
      <form
        onSubmit={(event) => event.preventDefault()}
        className="mx-auto w-full max-w-[1600px]"
      >
        <dl className="ml-auto grid w-full max-w-md grid-cols-1 rounded-lg border border-gray-200 bg-white text-sm sm:w-fit sm:max-w-none sm:grid-cols-[auto_auto]">
          <div className="min-w-0 px-4 py-3 sm:px-5">
            <dt className="text-xs font-medium text-slate-500">
              Numéro de formulaire
            </dt>
            <dd className="mt-1 break-words font-semibold text-slate-800">
              {formData.numeroFormulaire}
            </dd>
          </div>
          <div className="min-w-0 border-t border-gray-100 px-4 py-3 sm:border-l sm:border-t-0 sm:px-5">
            <dt className="text-xs font-medium text-slate-500">Date</dt>
            <dd className="mt-1 font-medium text-slate-700">
              <time dateTime={formData.dateSoumission}>
                {new Intl.DateTimeFormat("fr-CA", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                  timeZone: "UTC",
                }).format(new Date(formData.dateSoumission))}
              </time>
            </dd>
          </div>
        </dl>

        <div className="my-6 flex w-full justify-center">
          <div className="w-fit max-w-full rounded-lg border border-blue-100 border-l-4 border-l-blue-500 bg-blue-50 px-4 py-3 text-blue-800 shadow-sm">
            <p className="flex items-center gap-2 text-lg font-semibold">
              <AcademicCapIcon className="h-5 w-5 flex-none text-blue-600" />
              Demande de participation à une formation
            </p>
          </div>
        </div>

        <SectionHeader icon={UserIcon}>
          Information du membre du personnel
        </SectionHeader>

        <div className={sectionCardClassName}>
          <div className="p-5 sm:p-6">
            <div className="mb-4">
              <p className="text-sm font-semibold text-gray-800">
                Renseignements du profil
              </p>
              <p className="mt-1 text-[13px] leading-5 text-slate-600">
                Ces informations proviennent de votre compte professionnel.
              </p>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <ReadOnlyInfo label="Nom" value={formData.nom} />
              <ReadOnlyInfo label="Courriel" value={formData.courriel} />
              <ReadOnlyInfo label="Lieu de travail" value={formData.lieuTravail} />
              <ReadOnlyInfo label="Poste" value={formData.poste} />
            </div>
          </div>

          <div className="rounded-b-xl border-t border-gray-200 bg-white p-5 sm:p-6">
            <div className="mb-5">
              <p className="text-sm font-semibold text-gray-800">
                Responsables de l’approbation
              </p>
              <p className="mt-1 text-[13px] leading-5 text-slate-600">
                Recherchez et sélectionnez les personnes responsables.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <div className="w-full min-w-0">
                <label className="mb-2 block text-[15px] font-medium text-slate-700">
                  Supervision immédiate
                  <span className="ml-1 text-red-600">*</span>
                </label>
                <div className="relative [&_input]:text-[15px] [&_input::placeholder]:text-slate-500 [&_input::placeholder]:opacity-100">
                  {!formData.managerInfo.nom && (
                    <UsersList
                      onUserSelect={(selectedUser) =>
                        handleUserSelect(selectedUser, "managerInfo")
                      }
                      initialValue={formData.managerInfo.email}
                    />
                  )}

                  {formData.managerInfo.nom && (
                    <div className="flex min-h-[62px] items-center gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm">
                      <div className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-gray-100 text-sm font-semibold text-gray-600">
                        {formData.managerInfo.nom.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex min-w-0 flex-col">
                        <span className="break-words text-[15px] font-semibold text-slate-900">
                          {formData.managerInfo.nom}
                        </span>
                        <span className="break-words text-[13px] leading-5 text-slate-600">
                          {formData.managerInfo.email}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          setFormData((current) => ({
                            ...current,
                            managerInfo: {
                              nom: "",
                              email: "",
                              userId: "",
                              matricule: "",
                            },
                          }))
                        }
                        className="ml-auto flex h-8 w-8 flex-none items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-red-50 hover:text-red-500"
                        aria-label="Retirer la supervision immédiate"
                      >
                        ✕
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="w-full min-w-0">
                <label className="mb-2 block text-[15px] font-medium text-slate-700">
                  Direction de service / secteur / surintendance
                  <span className="ml-1 text-red-600">*</span>
                </label>
                <div className="relative [&_input]:text-[15px] [&_input::placeholder]:text-slate-500 [&_input::placeholder]:opacity-100">
                  {!formData.directionSecteurSurintendance.nom && (
                    <UsersList
                      onUserSelect={(selectedUser) =>
                        handleUserSelect(
                          selectedUser,
                          "directionSecteurSurintendance",
                        )
                      }
                      initialValue={formData.directionSecteurSurintendance.email}
                    />
                  )}

                  {formData.directionSecteurSurintendance.nom && (
                    <div className="flex min-h-[62px] items-center gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm">
                      <div className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-gray-100 text-sm font-semibold text-gray-600">
                        {formData.directionSecteurSurintendance.nom
                          .charAt(0)
                          .toUpperCase()}
                      </div>
                      <div className="flex min-w-0 flex-col">
                        <span className="break-words text-[15px] font-semibold text-slate-900">
                          {formData.directionSecteurSurintendance.nom}
                        </span>
                        <span className="break-words text-[13px] leading-5 text-slate-600">
                          {formData.directionSecteurSurintendance.email}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          setFormData((current) => ({
                            ...current,
                            directionSecteurSurintendance: {
                              nom: "",
                              email: "",
                              userId: "",
                              matricule: "",
                            },
                          }))
                        }
                        className="ml-auto flex h-8 w-8 flex-none items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-red-50 hover:text-red-500"
                        aria-label="Retirer la direction sélectionnée"
                      >
                        ✕
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <SectionHeader icon={AcademicCapIcon}>Description de la sortie</SectionHeader>

        <div className={sectionCardClassName}>
          <div className="space-y-5 p-5 sm:p-6">
            <div>
              <p className="text-sm font-semibold text-gray-800">Renseignements sur la formation</p>
              <p className="mt-1 text-[13px] leading-5 text-slate-600">
                Précisez les objectifs, le lieu et la durée de la formation.
              </p>
            </div>
            <div>
          <label htmlFor="descriptionObjectifs" className={labelClassName}>
            Courte description et objectifs<span className="text-xl text-red-900"> *</span> :
          </label>
          <textarea
            id="descriptionObjectifs"
            name="descriptionObjectifs"
            value={formData.descriptionObjectifs}
            onChange={handleChange}
            rows={5}
            required
            className={`${inputClassName} resize-y`}
          />
            </div>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-6">
          <Field label="Ville" name="ville" value={formData.ville} onChange={handleChange} required />
          <Field label="Province ou pays" name="provincePays" value={formData.provincePays} onChange={handleChange} required />
        </div>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-6">
          <div className="min-w-0 text-[15px]">
            <p className={labelClassName}>
              Date de début<span className="text-xl text-red-900"> *</span> :
            </p>
            <ModernDatePicker
              value={formData.dateDebut}
              onChange={(date) =>
                handleChange({ target: { name: "dateDebut", value: date } })
              }
              ariaLabel="Date de début de la formation (obligatoire)"
            />
          </div>
          <Field label="Nombre de jours" name="nombreJours" value={formData.nombreJours} onChange={handleChange} type="number" min="1" required />
        </div>

        <div>
          <YesNoField
            label="La formation nécessite-t-elle un voyage à l’extérieur de l’Ontario?"
            name="voyageExterieurOntario"
            value={formData.voyageExterieurOntario}
            onChange={handleChange}
            required
          />
        </div>

          </div>

          <div className="space-y-5 rounded-b-xl border-t border-gray-200 p-5 sm:p-6">
            <div>
              <p className="text-sm font-semibold text-gray-800">Besoins de suppléance</p>
              <p className="mt-1 text-[13px] leading-5 text-slate-600">
                Indiquez si un remplacement est nécessaire pendant votre absence.
              </p>
            </div>
        <div>
          <YesNoField label="Suppléance requise" name="suppleanceRequise" value={formData.suppleanceRequise} onChange={handleChange} required />
        </div>

        {formData.suppleanceRequise === "oui" && (
          <>
            <div className="grid grid-cols-1 items-start gap-5 sm:grid-cols-2 sm:gap-6">
              <Field label="Nombre de jours de suppléance" name="nombreJoursSuppleance" value={formData.nombreJoursSuppleance} onChange={handleChange} type="number" min="1" required />
              <YesNoField label="Suppléance payée de fonds centraux" name="suppleanceFondsCentraux" value={formData.suppleanceFondsCentraux} onChange={handleChange} required />
            </div>

            {formData.suppleanceFondsCentraux === "oui" && (
              <div>
                <label htmlFor="justificationFondsCentraux" className={labelClassName}>
                  Justification pour une suppléance payée de fonds centraux<span className="text-xl text-red-900"> *</span> :
                </label>
                <textarea
                  id="justificationFondsCentraux"
                  name="justificationFondsCentraux"
                  value={formData.justificationFondsCentraux}
                  onChange={handleChange}
                  rows={4}
                  required
                  className={`${inputClassName} resize-y`}
                />
              </div>
            )}
          </>
        )}

          </div>
        </div>

        <SectionHeader icon={CurrencyDollarIcon} iconColor="text-yellow-400">
          Estimation des coûts
        </SectionHeader>

        <div className="mt-5 w-full overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm lg:w-4/5 xl:w-3/4">
          <div className="grid grid-cols-1 gap-x-6 gap-y-5 p-5 sm:grid-cols-2 sm:p-6">
            <MoneyField
              label="Frais d’inscription"
              name="coutInscription"
              value={formData.coutInscription}
              onChange={handleChange}
            />
            <fieldset className="min-w-0">
              <legend className="sr-only">Transport</legend>
              <div className="flex flex-wrap items-end gap-3">
                <span className="mr-auto flex h-10 items-center text-[15px] font-medium text-slate-700" aria-hidden="true">
                  Transport :
                </span>
                <div className="w-32 max-w-full">
                  <label htmlFor="kilometrageTransport" className="text-[13px] text-slate-600">
                    Kilométrage
                  </label>
                  <div className="relative mt-1">
                    <input
                      id="kilometrageTransport"
                      name="kilometrageTransport"
                      type="text"
                      inputMode="decimal"
                      value={formData.kilometrageTransport}
                      aria-describedby="transportRate"
                      onChange={(event) => {
                        const value = event.target.value.replace(",", ".");
                        if (/^\d*(\.\d*)?$/.test(value)) {
                          handleChange({ target: { name: "kilometrageTransport", value } });
                        }
                      }}
                      className="h-10 w-full rounded-lg border border-slate-300 bg-white py-2 pl-3 pr-9 text-right text-[15px] text-slate-800 shadow-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    />
                    <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs text-slate-500">km</span>
                  </div>
                </div>
                <div className="w-36 max-w-full">
                  <label htmlFor="coutTransport" className="text-[13px] text-slate-600">
                    Montant calculé
                  </label>
                  <div className="relative mt-1">
                    <input
                      id="coutTransport"
                      type="text"
                      value={coutTransport}
                      readOnly
                      placeholder="—"
                      aria-describedby="transportRate"
                      className="h-10 w-full rounded-lg border border-slate-200 bg-slate-50 py-2 pl-3 pr-9 text-right text-[15px] text-slate-800 outline-none focus:ring-2 focus:ring-blue-100"
                    />
                    <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-slate-500">$</span>
                  </div>
                </div>
              </div>
              <p id="transportRate" role="status" className="mt-2 text-[13px] leading-5 text-slate-600">
                {mileageMessage}
              </p>
            </fieldset>
            <MoneyField
              label="Hébergement"
              name="coutHebergement"
              value={formData.coutHebergement}
              onChange={handleChange}
            />
            <MoneyField
              label="Repas"
              name="coutRepas"
              value={formData.coutRepas}
              onChange={handleChange}
            />
            <div className="sm:col-span-2 sm:max-w-[calc(50%-0.75rem)]">
              <MoneyField
                label="Suppléance (235 $ par jour)"
                name="coutSuppleance"
                value={formData.coutSuppleance}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="border-t border-slate-200 bg-slate-50 p-5 sm:p-6">
            <p className="mb-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
              Autre dépense
            </p>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-6">
              <Field
                label="Description"
                name="autreDescription"
                value={formData.autreDescription}
                onChange={handleChange}
              />
              <MoneyField
                label="Montant"
                name="coutAutre"
                value={formData.coutAutre}
                onChange={handleChange}
              />
            </div>

            <div className="mt-5 flex items-center justify-between border-t border-slate-200 pt-5">
              <p className="text-sm font-semibold text-slate-700">
                {transportPending ? "Total partiel (transport non calculé)" : "Total estimé"}
              </p>
              <p className="text-xl font-bold text-slate-900">
                {formattedTotal}
              </p>
            </div>
          </div>
        </div>

        <SectionHeader icon={PencilAltIcon} iconColor="text-green-400">
          Signature du demandeur
        </SectionHeader>

        <div className={`${sectionCardClassName} p-5 sm:p-6`}>
          <label className="relative flex cursor-pointer items-start gap-4 sm:items-center">
            <input
              type="checkbox"
              name="signatureElectronique"
              checked={formData.signatureElectronique === "oui"}
              onChange={handleCertificationChange}
              className="absolute opacity-0 w-6 h-6"
              required
            />
            <span className={`flex h-6 w-6 flex-none items-center justify-center rounded-md border-2 transition-all duration-200 ${
              formData.signatureElectronique === "oui"
                ? "border-blue-500 bg-blue-50"
                : "border-slate-300 bg-slate-50"
            }`}>
              {formData.signatureElectronique === "oui" && (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-10 w-10 text-blue-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={3}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              )}
            </span>
            <span className="select-none text-sm font-medium leading-6 text-slate-900">
              Je certifie que les informations fournies sont exactes, complètes
              et vérifiées.
            </span>
          </label>
        </div>

        <div className="mt-12 flex justify-start pb-4">
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg border border-slate-700 bg-slate-700 px-3.5 py-2 text-sm font-semibold text-white shadow-sm transition-colors duration-150 hover:border-slate-600 hover:bg-slate-600 active:bg-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2"
            >
              <FiSave className="h-4 w-4 flex-none" aria-hidden="true" />
              Enregistrer
            </button>
            <button
              type="button"
              className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg border border-green-700 bg-green-700 px-3.5 py-2 text-sm font-semibold text-white shadow-sm transition-colors duration-150 hover:border-green-800 hover:bg-green-800 active:bg-green-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-700 focus-visible:ring-offset-2"
            >
              <FiSend className="h-4 w-4 flex-none" aria-hidden="true" />
              Soumettre
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default ParticipationFormation;
